import { motion } from 'framer-motion';
import Link from 'next/link';
import React from 'react';
import { PressedMotion } from '@/components/elements/PressedMotion';
import { Button } from '@/components/UI';
import { useTranslationContext } from '@/context/translation';
import { useTranslationWithLocale, useWindowSize } from '@/hooks';
import { bannerMotion } from '../../animation';
import { CSS_VARIABLE_NAMES, ID_KEYS } from '../../constants';
import { useSetCookies } from '../../hooks';
import styles from './CookiesBanner.module.scss';

export const CookiesBanner = (
	props: unknown,
	ref: React.Ref<HTMLDivElement>,
) => {
	// CookiesBanner is rendered in CookiesProvider which is above TranslationProvider in the tree
	// So we need to load translations via API, not from context
	// Try to get locale from context first, but fallback to URL/cookies if not available
	const { locale: contextLocale, translations } = useTranslationContext();
	const hasContextTranslations =
		translations?.common && Object.keys(translations.common).length > 0;

	// Use context locale and translations only if they exist, otherwise let hook determine from URL/cookies
	const { t } = useTranslationWithLocale(
		'common',
		hasContextTranslations && contextLocale ? contextLocale : undefined,
		hasContextTranslations
			? (translations?.common as Record<string, unknown>)
			: undefined,
	);
	const choseCookies: (type: 'save' | 'reject' | 'accept' | 'popup') => void =
		useSetCookies();
	const windowSize = useWindowSize();

	// Effects
	React.useEffect(() => {
		const bannerHeight =
			document.getElementById(ID_KEYS.BANNER)?.getBoundingClientRect().height ??
			0;
		document.documentElement.style.setProperty(
			CSS_VARIABLE_NAMES.BANNER_HEIGHT,
			`${bannerHeight}px`,
		);
	}, [windowSize]);
	React.useEffect(() => {
		return () => {
			document.documentElement.style.setProperty(
				CSS_VARIABLE_NAMES.BANNER_HEIGHT,
				'0',
			);
		};
	}, []);

	return (
		<motion.div
			ref={ref}
			id={ID_KEYS.BANNER}
			className={styles['banner']}
			// Animation
			{...bannerMotion}
		>
			<p className={styles['banner__text']}>
				{t('cookies.bannerText')}{' '}
				<Link
					className={styles['banner__link']}
					href='/cookie-policy'
					prefetch={false}
				>
					{t('links.cookiePolicy')}
				</Link>
			</p>
			<div className={styles['banner__actions']}>
				<PressedMotion
					className={styles['banner__button-motion']}
					pressPower={0.95}
				>
					<Button
						as='button'
						text={t('cookies.cookieSettings')}
						preset='secondary'
						onClick={() => choseCookies('popup')}
						classes={{
							button: styles['banner__button'],
						}}
					/>
				</PressedMotion>
				<PressedMotion
					className={styles['banner__button-motion']}
					pressPower={0.95}
				>
					<Button
						as='button'
						text={t('cookies.acceptAll')}
						onClick={() => choseCookies('accept')}
						classes={{
							button: styles['banner__button'],
						}}
					/>
				</PressedMotion>
			</div>
		</motion.div>
	);
};
export const CookiesBannerRef = React.forwardRef(CookiesBanner);
